import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdminService from '../../services/AdminService';
import { format } from 'date-fns';
import { debounce } from 'lodash';

const getActionColor = (action) => {
  switch (action?.toLowerCase()) {
    case 'login':
      return 'primary';
    case 'logout':
      return 'secondary';
    case 'create':
      return 'success';
    case 'update':
    case 'edit':
      return 'info';
    case 'delete':
      return 'error';
    case 'search':
    case 'view':
      return 'warning';
    case 'export_data':
      return 'success';
    case 'view_dashboard':
    case 'view_audit_logs':
    case 'view_feedback':
    case 'view_query_logs':
    case 'view_users':
    case 'view_alerts':
      return 'info';
    default:
      return 'default';
  }
};

const getActionDisplayName = (action) => {
  const actionMap = {
    'view_audit_logs': 'View Audit Logs',
    'view_feedback': 'View Feedback',
    'view_query_logs': 'View Query Logs', 
    'view_users': 'View Users',
    'view_alerts': 'View Alerts',
    'view_dashboard': 'View Dashboard',
    'export_data': 'Export Data',
    'check_profile': 'Check Profile'
  };
  
  return actionMap[action] || action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25); // Start with more rows
  const [totalLogs, setTotalLogs] = useState(0);
  
  // Filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [username, setUsername] = useState('');
  const [action, setAction] = useState('');
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  
  // Debounced filter function
  const debouncedFetchLogs = useCallback(
    debounce((filters) => {
      fetchLogsInternal(filters);
    }, 300),
    []
  );
  
  const getCurrentFilters = useCallback(() => {
    return {
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null,
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
      username: username || null,
      action: action || null
    };
  }, [startDate, endDate, username, action]);
    const fetchLogsInternal = useCallback(async (overrideFilters = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = overrideFilters || getCurrentFilters();
      
      // Use page directly since the backend now expects page numbers (1-indexed)
      const params = {
        page: page + 1, // Convert 0-based to 1-based pagination
        limit: rowsPerPage,
        sort_by: 'timestamp',
        sort_order: 'desc' // Latest first
      };
      
      // Add filters to params only if they have values
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params[key] = filters[key];
        }
      });
      
      console.log('Fetching audit logs with params:', params);
      
      // Clear cache before making a new request to ensure fresh data
      AdminService.clearCacheForEndpoint('/api/admin/audit-logs');
      const response = await AdminService.getAuditLogs(params);
      
      if (response && response.logs) {
        // Filter out potential duplicates on the frontend as well
        const uniqueLogs = removeDuplicateLogs(response.logs);
        setLogs(uniqueLogs);
        setTotalLogs(response.total || uniqueLogs.length);
        console.log(`Loaded ${uniqueLogs.length} unique logs out of ${response.logs.length} total`);
      } else {
        console.warn('No logs in response:', response);
        setLogs([]);
        setTotalLogs(0);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setError('Failed to load audit logs. Please try again.');
      setLogs([]);
      setTotalLogs(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, getCurrentFilters]);
  
  // Remove duplicate logs based on user, action, resource, and timestamp proximity
  const removeDuplicateLogs = (logs) => {
    const seen = new Set();
    const filtered = [];
    
    logs.forEach(log => {
      // Create a key based on user, action, resource and rounded timestamp (to nearest 5 seconds)
      const timestamp = new Date(log.timestamp);
      const roundedTime = Math.floor(timestamp.getTime() / 5000) * 5000; // Round to nearest 5 seconds
      
      const key = `${log.user_id || log.username}:${log.action}:${log.resource}:${roundedTime}`;
      
      if (!seen.has(key)) {
        seen.add(key);
        filtered.push(log);
      }
    });
    
    return filtered;
  };
  
  // Memoized fetch function to prevent unnecessary re-renders
  const fetchLogs = useCallback((overrideFilters = null) => {
    fetchLogsInternal(overrideFilters);
  }, [page, rowsPerPage, fetchLogsInternal]);
  
  // Effect for pagination changes
  useEffect(() => {
    fetchLogs();
  }, [page, rowsPerPage, lastRefresh, fetchLogs]);
  const handleChangePage = (event, newPage) => {
    console.log('Changing page to:', newPage + 1);
    // Force a data refresh by clearing the cache first
    AdminService.clearCacheForEndpoint('/api/admin/audit-logs');
    setPage(newPage);
    // The useEffect hook will trigger fetchLogs due to the page dependency
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log('Changing rows per page to:', newRowsPerPage);
    // Force a data refresh by clearing the cache first
    AdminService.clearCacheForEndpoint('/api/admin/audit-logs');
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // The useEffect hook will trigger fetchLogs due to the rowsPerPage dependency
  };
  
  const handleApplyFilters = () => {
    console.log('Applying filters');
    setPage(0);
    const filters = getCurrentFilters();
    console.log('Applied filters:', filters);
    
    // Clear cache for this endpoint to ensure fresh data
    AdminService.clearCacheForEndpoint('/api/admin/audit-logs');
    fetchLogs(filters);
  };
  
  const handleClearFilters = () => {
    console.log('Clearing filters');
    setStartDate(null);
    setEndDate(null);
    setUsername('');
    setAction('');
    setPage(0);
    
    // Clear cache and fetch with no filters
    AdminService.clearCacheForEndpoint('/api/admin/audit-logs');
    
    const clearedFilters = {
      start_date: null,
      end_date: null,
      username: null,
      action: null
    };
    
    fetchLogs(clearedFilters);
  };
  
  const handleRefresh = () => {
    console.log('Manual refresh');
    AdminService.clearCacheForEndpoint('/api/admin/audit-logs');
    setLastRefresh(Date.now());
  };
  
  const handleExportLogs = async () => {
    try {
      console.log('Starting export...');
      const filters = getCurrentFilters();
      
      const params = {
        ...filters,
        sort_by: 'timestamp',
        sort_order: 'desc',
        format: 'csv'
      };
      
      // Remove null values
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      console.log('Export params:', params);
      
      await AdminService.exportAuditLogs(params);
      console.log('Export completed successfully');
      
    } catch (error) {
      console.error('Error exporting logs:', error);
      setError('Failed to export logs. Please try again.');
    }
  };
  
  // Memoized action options to prevent re-renders
  const actionOptions = useMemo(() => [
    { value: '', label: 'All Actions' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'view', label: 'View' },
    { value: 'get', label: 'Get' },
    { value: 'options', label: 'Options' },
    { value: 'delete', label: 'Delete' },
    { value: 'export_data', label: 'Export Data' }
  ], []);
  
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  const formatMetadata = (metadata) => {
    if (!metadata || typeof metadata !== 'object') return 'N/A';
    
    try {
      // Extract useful information from metadata
      const parts = [];
      if (metadata.duration) parts.push(`Duration: ${metadata.duration}`);
      if (metadata.status_code) parts.push(`Status: ${metadata.status_code}`);
      if (metadata.query_params && metadata.query_params !== '""') {
        parts.push(`Params: ${metadata.query_params}`);
      }
      
      return parts.length > 0 ? parts.join(' | ') : 'No details';
    } catch (error) {
      return 'Invalid metadata';
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Audit Logs</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    size: 'small'
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    size: 'small'
                  },
                }}
              />
            </Grid>
          </LocalizationProvider>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              size="small"
              placeholder="Enter username"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={action}
                onChange={(e) => setAction(e.target.value)}
                label="Action"
              >
                {actionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleApplyFilters}
                sx={{ flexGrow: 1 }}
                disabled={loading}
                size="small"
              >
                Apply
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                size="small"
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Audit Log Entries
          {totalLogs > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({totalLogs.toLocaleString()} total)
            </Typography>
          )}
        </Typography>
        
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportLogs}
          disabled={loading}
          size="small"
        >
          Export CSV
        </Button>
      </Box>
      
      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="body2">
            {error}
          </Typography>
        </Paper>
      )}
      
      <Paper>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 160 }}>Timestamp</TableCell>
                <TableCell sx={{ minWidth: 120 }}>User</TableCell>
                <TableCell sx={{ minWidth: 120 }}>IP Address</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Action</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Resource</TableCell>
                <TableCell sx={{ minWidth: 250 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading audit logs...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {error ? 'Error loading logs' : 'No audit logs found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, index) => (
                  <TableRow key={log.id || `log-${page}-${index}`} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {formatTimestamp(log.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.username || log.user_id || 'Anonymous'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {log.ip_address || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getActionDisplayName(log.action)}
                        size="small"
                        color={getActionColor(log.action)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={log.resource || 'N/A'} placement="top">
                        <Typography 
                          variant="body2" 
                          noWrap 
                          sx={{ 
                            maxWidth: 200,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem'
                          }}
                        >
                          {log.resource || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatMetadata(log.metadata)} placement="top">
                        <Typography 
                          variant="body2" 
                          noWrap 
                          sx={{ 
                            maxWidth: 250,
                            fontSize: '0.75rem'
                          }}
                        >
                          {formatMetadata(log.metadata)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalLogs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton={true}
          showLastButton={true}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${count !== -1 ? count.toLocaleString() : `more than ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
};

export default AuditLogs;